import { useEffect } from "react";

const SITE = {
  name: "CraftOnline",
  url: process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : ""),
  locale: "it_IT",
  defaultDescription:
    "CraftOnline: pensa un'idea e crea! Inizia a realizzare quello che ti piace con un po' di stoffa, colla, filo e ...un po' di fantasia e manualità!",
  twitterHandle: "",
};

function ensureHeadLinks() {
  // Performance: Usa solo link necessari. I font dovrebbero idealmente stare in index.html per evitare FOUT.
  const ensure = (selector, attrs, tag = "link") => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement(tag);
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  };

  // Preconnect per accelerare il download
  ensure('link[rel="preconnect"][href="https://fonts.googleapis.com"]', {
    rel: "preconnect",
    href: "https://fonts.googleapis.com",
  });
  ensure('link[rel="preconnect"][href="https://fonts.gstatic.com"]', {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossorigin: "anonymous", // Importante per performance
  });
  
  // Font caricato con display=swap per evitare testo invisibile
  ensure('link[data-seo-id="google-fonts"]', {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Pacifico&family=Quicksand:wght@300;400;600;700&display=swap",
    "data-seo-id": "google-fonts",
  });
}

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setCanonical(href) {
  // Rimuove eventuali duplicati prima di settare
  const existing = document.head.querySelectorAll('link[rel="canonical"]');
  existing.forEach(el => el.remove());

  const link = document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", href);
  document.head.appendChild(link);
}

function upsertJsonLd(id, json) {
  const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json); // textContent è più sicuro di .text
}

function removeJsonLd(id) {
  const el = document.head.querySelector(
    `script[type="application/ld+json"][data-seo-id="${id}"]`
  );
  if (el) el.remove();
}

function safeText(v) {
  if (!v) return "";
  return String(v).replace(/\s+/g, " ").trim();
}

export default function SEO({ view, project }) {
  useEffect(() => {
    ensureHeadLinks();

    const path =
      view === "product" && project?.id != null
        ? `/progetto/${project.id}`
        : view === "portfolio"
          ? "/portfolio"
          : view === "about"
            ? "/chi-sono"
            : "/";

    const url = new URL(path, SITE.url).toString();

    const titleBase = SITE.name;
    const pageTitle =
      view === "product" && project
        ? `${safeText(project.title)} | ${titleBase}`
        : view === "portfolio"
          ? `Portfolio Lavori | ${titleBase}` // Keyword "Lavori" aggiunta
          : view === "about"
            ? `Chi sono Monica | ${titleBase}` // Keyword "Monica" aggiunta
            : titleBase;

    const description =
      view === "product" && project
        ? safeText(project.description || project.longDescription || SITE.defaultDescription).substring(0, 160) // Taglio a 160 caratteri per Google
        : SITE.defaultDescription;

    document.title = pageTitle;
    setCanonical(url);

    // Basic Meta
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index,follow,max-image-preview:large" }); // max-image-preview aiuta Discover

    // Open Graph
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: view === "product" ? "article" : "website", // 'article' è meglio per singoli progetti
    });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE.name });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: SITE.locale });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: pageTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    
    const ogImage = project?.img || `${SITE.url}/logo512.png`; // Fallback al logo PNG invece che SVG (più compatibile con Social)
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });

    // JSON-LD: WebSite
    upsertJsonLd("website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.name,
      url: SITE.url,
      inLanguage: "it-IT",
      "sameAs": [
        "https://www.facebook.com/monicamcroll/",
        "https://www.instagram.com/monica_craftonline/",
        "https://www.youtube.com/c/CraftOnLine-Italy/featured"
      ]
    });

    // JSON-LD: Project (Product/CreativeWork)
    if (view === "product" && project) {
      upsertJsonLd("project", {
        "@context": "https://schema.org",
        "@type": ["CreativeWork", "Product"],
        name: safeText(project.title),
        description: description,
        image: project.img ? [project.img] : undefined,
        url: url,
        creator: {
            "@type": "Person",
            "name": "Monica Mattarollo"
        },
        keywords: Array.isArray(project.tags) ? project.tags.join(", ") : "Cucito Creativo, Fatto a mano",
        datePublished: project.createdAt ? new Date(project.createdAt).toISOString() : undefined
      });
    } else {
      removeJsonLd("project");
    }
  }, [view, project]);

  return null;
}